import { Suspense } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { motion } from 'framer-motion';

import { useAuth } from '../../auth/auth-context';
import { homeRouteFor } from '../../lib/access';
import { webEnv } from '../../lib/env';
import { MOTION_DURATION, MOTION_EASE } from '../../lib/motion';
import { RouteFallback } from '../common/route-fallback';
import { DeviceLocationProvider } from '../location/device-location-provider';
import { AppLogo } from './app-logo';
import { MobileNavigation } from './mobile-navigation';
import { SidebarNavigation } from './sidebar-navigation';
import { ThemeToggle } from './theme-toggle';
import { UserMenu } from './user-summary';

/**
 * Application shell for every authenticated screen.
 *
 * A fixed sidebar from `lg` up holds the brand lockup and the primary navigation;
 * below `lg` the same list moves into a slide-over opened from the header. The
 * header carries the account menu (Profile, Logout). What the sidebar lists depends
 * on the server-computed capabilities (`sidebar-navigation.tsx`) and is
 * presentation only.
 *
 * It also holds the device-location choice (ADR-43). There is no prompt on
 * sign-in: users opt in from Profile → Device location, and nothing is read
 * until they click "Share location" there.
 *
 * Content follows `docs/ui-guidelines.md` section 4: `max-w-6xl` measure, a 16 px
 * gutter that holds at 360 px, and vertical rhythm in multiples of 4.
 */
export function AppShell() {
  const { user } = useAuth();
  const location = useLocation();
  const home = user === null ? null : homeRouteFor(user);

  return (
    <DeviceLocationProvider>
      <div className="relative min-h-dvh bg-surface-base">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:rounded-md focus:border focus:border-brand-400 focus:bg-surface-overlay focus:px-4 focus:py-2 focus:text-sm focus:text-ink-primary"
        >
          Skip to main content
        </a>

        <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-line-subtle bg-surface-sunken lg:flex">
          <div className="flex h-16 shrink-0 items-center border-b border-line-subtle px-5">
            {home === null ? (
              <AppLogo />
            ) : (
              <Link
                to={home}
                className="min-w-0 rounded-md"
                aria-label={`${webEnv.appName} — home`}
              >
                <AppLogo />
              </Link>
            )}
          </div>

          <nav aria-label="Primary" className="flex-1 overflow-y-auto px-3 py-4">
            <SidebarNavigation />
          </nav>

          <p className="shrink-0 border-t border-line-subtle px-5 py-4 text-xs leading-relaxed text-ink-muted">
            Authorized access only. Every project open is recorded.
          </p>
        </aside>

        <div className="flex min-h-dvh flex-col lg:pl-64">
          <header className="sticky top-0 z-20 border-b border-line-subtle bg-surface-base/95">
            <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
              <div className="flex min-w-0 items-center gap-3 lg:hidden">
                <MobileNavigation />
                <AppLogo />
              </div>
              {/* Keeps the account menu right-aligned when the lockup is in the sidebar. */}
              <span aria-hidden="true" className="hidden lg:block" />

              <div className="flex shrink-0 items-center gap-2">
                <ThemeToggle />
                {user === null ? null : <UserMenu user={user} />}
              </div>
            </div>
          </header>

          <main
            id="main-content"
            className="relative mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6 md:py-10"
          >
            {/*
            Keyed on the path so the content fades up once per navigation, never on
            re-render. Opacity and an 8 px rise only, per ui-guidelines section 9.
          */}
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: MOTION_DURATION, ease: MOTION_EASE }}
            >
              <Suspense fallback={<RouteFallback />}>
                <Outlet />
              </Suspense>
            </motion.div>
          </main>

          <footer className="border-t border-line-subtle">
            <div className="mx-auto flex w-full max-w-6xl flex-col gap-1 px-4 py-5 text-xs text-ink-muted sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <span>
                © {new Date().getFullYear()} {webEnv.companyName}
              </span>
              <span>{webEnv.appName}</span>
            </div>
          </footer>
        </div>
      </div>
    </DeviceLocationProvider>
  );
}
